import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { APP_CONFIG } from '@core/common-legacy';
import { createActionStream } from '@core/ngrx-legacy';
import {
  TellentAppsDataService,
  TellentCurrentUserSelectors,
  TellentMembersActions,
} from '@core/tellent-data';
import {
  AddEmployeeForPreonboardingPayload,
  AddEmployeeWithoutAccessPayload,
  Seat,
  SeatKind,
  TellentIAppConfig,
  TellentMember,
} from '@core/tellent-types';

import { TellentAccessService } from '../tellent-access.service';
import { TellentCurrentUserDataFacade } from '../tellent-current-user-data.facade';
import { TellentMembersDataFacade } from '../tellent-members-data.facade';

@Injectable({ providedIn: 'root' })
export class UserInvitationFacade {
  private readonly store = inject(Store);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly currentUserDataFacade = inject(TellentCurrentUserDataFacade);
  private readonly tellentAccessService = inject(TellentAccessService);
  private readonly tellentAppsService = inject(TellentAppsDataService);
  private readonly appConfig = <TellentIAppConfig>inject(APP_CONFIG);

  private readonly coreHrSeat = this.store.selectSignal(
    TellentCurrentUserSelectors.seatByKindSelector(SeatKind.CoreHR),
  );
  private readonly currentMembership = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe,
  );

  public readonly generateTellentPasswordsSuccess$ = createActionStream(
    TellentMembersActions.generateTellentPasswordsSuccess,
  );

  public readonly generateAppPasswordsSuccess$ = createActionStream(
    TellentMembersActions.generateAppPasswordsSuccess,
  );

  public companyLoginPage = computed(() => {
    const subdomain = this.currentMembership()?.org.subdomain;
    const authUrl = this.appConfig.tellentAuthUrl;

    return `${authUrl}/login/${subdomain}`;
  });

  // We identify grow-only organization based on a specific Manage subscription item id 'tellent_hr_base'
  public organizationHasGrowStandalone = computed(() => {
    const kiwiSeat = this.currentUserDataFacade.seatByKind(SeatKind.CoreHR) ?? undefined;
    return !!kiwiSeat()?.app.subscriptionItems.some(item => item.id.includes('tellent_hr_base'));
  });

  public organizationHasCoreHrInactive = toSignal(
    this.tellentAppsService.hasInactiveSubscription(SeatKind.CoreHR),
    { initialValue: false },
  );

  public generatePasswords(members: TellentMember[]): void {
    const membersGuids = members.map((member: TellentMember) => member.guid);
    if (this.tellentAccessService.canManageTellentUsers()) {
      this.store.dispatch(
        TellentMembersActions.generateTellentPasswords({ payload: { membersGuids } }),
      );
    } else {
      this.store.dispatch(
        TellentMembersActions.generateAppPasswords({ payload: { membersGuids } }),
      );
    }
  }

  public addEmployeeForPreonboarding(members: Partial<AddEmployeeForPreonboardingPayload>): void {
    const { access, appGuid } = <Seat>this.coreHrSeat();
    const payload = <AddEmployeeForPreonboardingPayload>{
      members,
      seats: [
        {
          appGuid,
          access,
          roleExternalId: 'employee',
        },
      ],
      sendInvitation: false,
    };

    if (this.tellentAccessService.canManageTellentUsers()) {
      this.membersDataFacade.inviteTellentMember(payload);
    } else {
      this.membersDataFacade.inviteAppMember(payload);
    }
  }

  public addEmployeeWithoutAccess(members: Partial<AddEmployeeWithoutAccessPayload>): void {
    const { access, appGuid } = <Seat>this.coreHrSeat();
    const payload = <AddEmployeeWithoutAccessPayload>{
      members,
      seats: [
        {
          appGuid,
          access,
          roleExternalId: '',
        },
      ],
      sendInvitation: false,
    };

    if (this.tellentAccessService.canManageTellentUsers()) {
      this.membersDataFacade.inviteTellentMember(payload);
    } else {
      this.membersDataFacade.inviteAppMember(payload);
    }
  }

  public downloadPasswords(blob: Blob): void {
    this.downloadBlob(blob, this.generatePasswordsFilename());
  }

  private generatePasswordsFilename(): string {
    return `tellent-users-details-${new Date().toISOString().slice(0, 19).replace('T', '_').replaceAll(':', '-')}.csv`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}
